import type {
  WThemeSearchResultsView,
  WThemeGroupSearchResultsView,
  ThemeSearchRequest,
  WThemeGroupDetailView,
  WThemeDetailView,
} from '@zola/svc-web-api-ts-client';
import type { GetServerSidePropsContext } from 'next';
import { parseQueryString } from '@zola-helpers/client/dist/es/util/getQueryParam';
import type { ChangeDesignFilterOptions } from 'components/manage/EditWebsite/EditWebsiteChangeDesignV2/EditWebsiteChangeDesignV2';
import { LocalThemeSearchRequest } from 'components/manage/EditWebsite/api/changeDesign';

export type PLPFilterOptions = {
  colors: string[];
  special_features: string[];
  motifs: string[];
};

const validThemeSorts = ['FEATURED', 'RECENTLY_ADDED', 'POPULARITY'];

const isValidThemeSort = (sortKey: unknown): false | ThemeSearchRequest.SortKeyEnum => {
  if (typeof sortKey !== 'string') {
    return false;
  }
  if (!validThemeSorts.includes(sortKey.toUpperCase())) {
    return false;
  }
  return (sortKey.toUpperCase() as unknown) as ThemeSearchRequest.SortKeyEnum;
};

export const mapColorsToFilters = (
  colors: string[] | null
): {
  type: string;
  value: string;
}[] => {
  if (!colors) return [];
  return colors.map(value => ({ type: 'COLOR', value }));
};

type TrackingFilterOption = {
  type: string;
  value: string;
};

export function flatMapFormFilters(formFilters: PLPFilterOptions): TrackingFilterOption[] {
  const filters: TrackingFilterOption[] = [];
  const filterGroups = Object.keys(formFilters) as (keyof PLPFilterOptions)[];
  filterGroups.forEach(groupName => {
    formFilters[groupName].forEach(filterkey => {
      filters.push({
        type: groupName.toUpperCase(),
        value: filterkey,
      });
    });
  });
  return filters;
}

export const createThemesQueryStr = (
  filters: PLPFilterOptions,
  sortKey = ('FEATURED' as unknown) as ThemeSearchRequest.SortKeyEnum
): string => {
  const { colors = [], special_features = [], motifs = [] } = filters;
  const queries = [];
  if (colors.length) {
    queries.push(`colors=${colors.join(',')}`);
  }
  if (special_features.length) {
    queries.push(`special_features=${special_features.join(',')}`);
  }
  if (motifs.length) {
    queries.push(`motifs=${motifs.join(',')}`);
  }
  if (((sortKey as unknown) as string) !== 'FEATURED' && isValidThemeSort(sortKey)) {
    queries.push(`s=${((sortKey as unknown) as string).toLowerCase()}`);
  }
  const filterStr = queries.join('&');
  return filterStr.length ? `?${filterStr}` : '';
};

export const parseThemesQuery = (queryString: string): PLPFilterOptions | undefined => {
  if (!queryString) {
    return undefined;
  }
  const colorsStr = parseQueryString(queryString, 'colors');
  const motifsStr = parseQueryString(queryString, 'motifs');
  const specialFeaturesStr = parseQueryString(queryString, 'special_features');

  return {
    colors: colorsStr.length > 0 ? colorsStr.split(',') : [],
    special_features: specialFeaturesStr.length > 0 ? specialFeaturesStr.split(',') : [],
    motifs: motifsStr.length > 0 ? motifsStr.split(',') : [],
  };
};

export const parseThemesQueryWebsiteV2 = (
  queryString: string
): ChangeDesignFilterOptions | undefined => {
  if (!queryString) {
    return undefined;
  }
  const colorsStr = parseQueryString(queryString, 'colors');
  const motifsStr = parseQueryString(queryString, 'motifs');

  return {
    colors: colorsStr.length > 0 ? colorsStr.split(',') : [],
    motifs: motifsStr.length > 0 ? motifsStr.split(',') : [],
  };
};

export const parseNextQuery = (
  query: GetServerSidePropsContext['query']
): { filters: PLPFilterOptions; sortKey?: ThemeSearchRequest.SortKeyEnum } => {
  const { colors, motifs, special_features } = query;
  const { s } = query;
  const sortKey =
    isValidThemeSort(s) || (('FEATURED' as unknown) as ThemeSearchRequest.SortKeyEnum);
  const filters = {
    colors: colors && colors.length > 0 ? (colors as string).split(',') : [],
    special_features:
      special_features && special_features.length > 0
        ? (special_features as string).split(',')
        : [],
    motifs: motifs && motifs.length > 0 ? (motifs as string).split(',') : [],
  };
  return {
    filters,
    sortKey,
  };
};

export const updateQueryParams = (
  filters: PLPFilterOptions,
  sortKey = ('FEATURED' as unknown) as ThemeSearchRequest.SortKeyEnum
): void => {
  if (typeof window !== 'undefined') {
    window.history.pushState(
      {},
      '',
      window.location.href.split('?')[0] + createThemesQueryStr(filters, sortKey)
    );
  }
};

export const mapFiltersToThemeSearchRequestWebsiteV2 = ({
  filters = { colors: [], special_features: [], motifs: [] },
  sortKey = ('FEATURED' as unknown) as ThemeSearchRequest.SortKeyEnum,
  offset = 0,
  limit = 12,
  theme_group_keys = [],
}: {
  filters?: PLPFilterOptions;
  sortKey?: ThemeSearchRequest.SortKeyEnum;
  offset?: number;
  limit?: number;
  theme_group_keys?: string[];
}): LocalThemeSearchRequest => {
  const { colors, motifs } = filters;

  return {
    offset,
    limit,
    colors,
    motifs,
    sort_key: sortKey,
    theme_group_keys,
  };
};

export const getThemeGroupUniqueIdentifier = (
  group?: WThemeGroupSearchResultsView | WThemeGroupDetailView
): string | undefined => {
  return group?.key;
};

export const getThemeUniqueIdentifier = (
  theme?: WThemeSearchResultsView | WThemeDetailView
): string | undefined => {
  return theme?.key;
};

export const getThemePreviewImage = (theme?: WThemeSearchResultsView): string | undefined => {
  if (!theme) {
    return undefined;
  }
  const plpAsset = theme?.assets.filter(img => ((img.type as unknown) as string) === 'PLP')[0];
  return plpAsset?.image_url ? `${plpAsset?.image_url}?w=640&fm=webp` : undefined;
};

export const getThemePdpPreviewImage = (
  theme?: WThemeDetailView,
  activeLayout?: string
): string | undefined => {
  let previewImage;
  if (theme) {
    if (activeLayout) {
      const layoutAssets = theme?.assets_by_layout[activeLayout];
      if (layoutAssets && layoutAssets.length > 0) {
        const pdpAsset = layoutAssets.filter(layout => `${layout?.type}` === 'PDP');
        if (pdpAsset && pdpAsset[0]) {
          previewImage = pdpAsset[0]?.image_url
            ? `${pdpAsset[0].image_url}?w=1376&fm=webp`
            : undefined;
        }
      }
    } else {
      const pdpAsset = theme?.assets.filter(img => ((img.type as unknown) as string) === 'PDP')[0];
      previewImage = pdpAsset?.image_url ? `${pdpAsset?.image_url}?w=1376&fm=webp` : undefined;
    }
  }
  return previewImage;
};

export const getThemeSwatchColors = (
  theme?: WThemeSearchResultsView
): {
  primary: string | undefined;
  secondary: string | undefined;
} => {
  return {
    primary: theme?.swatch_color,
    secondary: theme?.swatch_color,
  };
};

export const getThemePdpSwatchColors = (
  theme?: WThemeDetailView
): {
  primary: string | undefined;
  secondary: string | undefined;
} => {
  return {
    primary: theme?.swatch_color,
    secondary: theme?.swatch_color,
  };
};

export const themeSupportsSinglePage = (theme?: WThemeDetailView): boolean => {
  return Boolean(theme?.layout_types.some(type => `${type}` === 'SINGLE_PAGE'));
};

export const isDisneyTheme = (theme?: WThemeSearchResultsView): boolean => {
  if (theme && theme.motifs && theme.motifs.length > 0) {
    return theme.motifs.filter(motif => motif.name === 'Disney').length > 0;
  }
  return false;
};
